import { TFunction } from 'i18next'
import {
  AuthChangePasswordReqBody,
  AuthChangePasswordSucResBody,
} from 'src/types/requests/auth/change-password'
import { AuthLoginReqBody, AuthLoginSucResBody } from 'src/types/requests/auth/login'
import {
  AuthRefreshReqBody,
  AuthRefreshRes,
  AuthRefreshResBody,
} from 'src/types/requests/auth/refresh'
import {
  RequestPasswordResetReqBody,
  RequestPasswordResetSucResBody,
} from 'src/types/requests/auth/request-password-reset'
import {
  ResetPasswordReqBody,
  ResetPasswordSucResBody,
} from 'src/types/requests/auth/reset-password'
import { apiPost, apiPut, apiUrl, getHttpInit } from 'src/utils/api'
import { multipleRegions } from 'src/utils/helperFunctions'
import { getRegionParam } from 'src/utils/region'
import { LoginHandleStateParams } from './types'

const URL_PREFIX = 'auth'

const TOKEN_KEY = 'token'

let refreshPromise: Promise<AuthRefreshResBody> | null = null

const authApiUrl = (path: string, params?: Record<string, string>) =>
  apiUrl(path, params, { prefix: URL_PREFIX })

/**
 * Calls the auth refresh endpoint. Returns the raw Response.
 * Callers typically use `renewToken()` instead to get a single in-flight request and stored token.
 */
export const getNewToken = async (): Promise<AuthRefreshRes> => {
  const url = authApiUrl('refresh')

  const payload: AuthRefreshReqBody = {
    token: localStorage.getItem(TOKEN_KEY),
    region: getRegionParam(),
  }

  const init = {
    ...getHttpInit('POST'),
    body: JSON.stringify(payload),
  }

  return await fetch(url, init)
}

/**
 * Renews the auth token and stores it. Deduplicates concurrent calls.
 */
export const renewToken = async (): Promise<AuthRefreshResBody> => {
  if (refreshPromise) return refreshPromise

  refreshPromise = (async (): Promise<AuthRefreshResBody> => {
    try {
      const response = await getNewToken()

      if (!response.ok) {
        const error = new Error(`Auth refresh failed: ${response.status}`) as Error & {
          response: Response
        }
        error.response = response

        throw error
      }

      const result = (await response.json()) as AuthRefreshResBody

      if (result.encodedToken) localStorage.setItem(TOKEN_KEY, result.encodedToken)

      return result
    } finally {
      refreshPromise = null
    }
  })()

  return refreshPromise
}

/**
 * Log in and update the token in local storage.
 * Does not set any state in the application (e.g., accounts), as it might be unnecessary
 * (for example, when simply needing a new token after updating the password).
 */
export const login = async (body: AuthLoginReqBody) => {
  const data = await apiPost<AuthLoginSucResBody>('login', {
    body,
    urlPrefix: URL_PREFIX,
    withAuth: false,
  })

  // Set the token
  localStorage.setItem(TOKEN_KEY, data.encodedToken)

  return data
}

export const handleLoginRes = (
  { accounts, encodedToken }: AuthLoginSucResBody,
  {
    setAlertComponent,
    setAccountList,
    setUserIsAuthenticated,
    setShouldQueryAccounts,
    navigate,
    confirmAccountSelection,
  }: LoginHandleStateParams,
  t: TFunction,
) => {
  if (!accounts.length) {
    setAlertComponent({
      open: true,
      severity: 'warning',
      message: t('messages.noAccountsAssociated'),
    })

    return []
  }

  const parsedAccounts = accounts.map((account) => {
    if (!account?.environmentalData?.data) {
      account.totalTrees = 0
      return account
    }

    account.totalTrees = account.environmentalData?.data.reduce(
      (total, { trees }) =>
        total + trees.reduce((totalTrees, currentValue) => totalTrees + currentValue.value, 0),
      0,
    )

    return account
  })

  setAccountList(parsedAccounts)
  setUserIsAuthenticated(true)
  setShouldQueryAccounts(false)

  if (multipleRegions(encodedToken) || parsedAccounts.length > 1) navigate('/region')
  else {
    confirmAccountSelection(parsedAccounts[0].fruitful_account_id, parsedAccounts)
    navigate('/dashboard')
  }
}

/**
 * Update the user's password.
 *
 * All existing tokens are invalidated by the API.
 * Therefore, make sure to call {@link login} after updating the password.
 */
export const updatePassword = async (body: AuthChangePasswordReqBody) =>
  await apiPut<AuthChangePasswordSucResBody>('password', { body, urlPrefix: URL_PREFIX })

export const requestPasswordReset = async (body: RequestPasswordResetReqBody) =>
  await apiPost<RequestPasswordResetSucResBody>('password/reset-request', {
    body,
    urlPrefix: URL_PREFIX,
    withAuth: false,
  })

/**
 * Reset the user's password using a token obtained from the recovery email.
 */
export const resetPassword = async (body: ResetPasswordReqBody) =>
  await apiPost<ResetPasswordSucResBody>('password/reset', {
    body,
    urlPrefix: URL_PREFIX,
    withAuth: false,
  })
